/**
 * Patient Registration Modal Component
 * Feature: 004-restore-missing-features
 * Task: T024, T025, T026, T027, T028, T029, T030
 *
 * Tabbed modal for patient registration:
 * - Search tab: Find and link to existing patients
 * - Create tab: Three-step wizard (patient data + family members + scheduling)
 */

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Search, Plus, AlertCircle, Users, CheckCircle, CalendarDays } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/features/auth'
import { useCreatePatient } from '../hooks/use-patients'
import { useFamilyMembers } from '../hooks/use-family-members'
import { PhoneInputWithDDI } from '@/features/shared/components/PhoneInputWithDDI'
import { usePatientSearch } from '../hooks/use-patient-search'
import { StepIndicator, type Step } from './StepIndicator'
import { FamilyMemberForm } from './FamilyMemberForm'
import { FamilyMemberCard } from './FamilyMemberCard'
import { PatientSearchResultCard } from './PatientSearchResultCard'
import type { RelationshipType } from '@/features/shared/types/patient.types'
import { ScheduleFormStep } from '@/features/scheduling/components/ScheduleFormStep'
import {
  createPatientFormSchema,
  type CreatePatientFormData,
} from '../schemas/patient-form.schema'

// ============================================================================
// Types
// ============================================================================

interface PatientRegistrationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

interface TempFamilyMember {
  id: string
  name: string
  email?: string
  phone?: string
  country_code: string
  relationship: RelationshipType
  is_primary_contact: boolean
  accepts_notifications: boolean
}

type Tab = 'search' | 'create'
type CreateStep = 'patient' | 'family' | 'scheduling'

const WIZARD_STEPS: Step[] = [
  { id: 'patient', label: 'Dados do Paciente' },
  { id: 'family', label: 'Familiares' },
  { id: 'scheduling', label: 'Agendamento' },
]

// ============================================================================
// Component
// ============================================================================

export function PatientRegistrationModal({
  open,
  onOpenChange,
  onSuccess,
}: PatientRegistrationModalProps) {
  // State
  const [activeTab, setActiveTab] = useState<Tab>('create')
  const [createStep, setCreateStep] = useState<CreateStep>('patient')
  const [tempFamilyMembers, setTempFamilyMembers] = useState<TempFamilyMember[]>([])
  const [isAddingMember, setIsAddingMember] = useState(false)
  const [editingMember, setEditingMember] = useState<TempFamilyMember | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [createdPatientId, setCreatedPatientId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Hooks
  const { currentOrganization } = useAuth()
  const createPatient = useCreatePatient()
  const familyMembersHook = useFamilyMembers()
  const patientSearch = usePatientSearch()

  // Form
  const form = useForm<CreatePatientFormData>({
    resolver: zodResolver(createPatientFormSchema),
    defaultValues: {
      full_name: '',
      birth_date: '',
      address: '',
      emergency_contact: '',
      emergency_contact_country_code: '55',
      condition_description: '',
      medical_history: '',
      current_medications: '',
    },
  })

  // Extract clearSearch to avoid dependency issues
  const { clearSearch } = patientSearch

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab('create')
      setCreateStep('patient')
      setTempFamilyMembers([])
      setIsAddingMember(false)
      setEditingMember(null)
      setError(null)
      setSuccessMessage(null)
      setCreatedPatientId(null)
      form.reset()
      clearSearch()
    }
  }, [open, form, clearSearch])

  // ============================================================================
  // Search Tab Handlers
  // ============================================================================

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    patientSearch.setSearchTerm(e.target.value)
  }

  const handleLinkToPatient = async (patientId: string) => {
    try {
      setError(null)
      await patientSearch.linkToPatient(patientId)
      setSuccessMessage('Paciente vinculado com sucesso!')
      setTimeout(() => {
        onOpenChange(false)
        onSuccess?.()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao vincular paciente')
    }
  }

  // ============================================================================
  // Create Tab Handlers
  // ============================================================================

  const handleNextStep = async () => {
    const isValid = await form.trigger()
    if (isValid) {
      setCreateStep('family')
    }
  }

  const handlePrevStep = () => {
    setCreateStep('patient')
  }

  const handleAddFamilyMember = (data: {
    name: string
    email?: string
    phone?: string
    country_code: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications: boolean
  }) => {
    const newMember: TempFamilyMember = {
      id: crypto.randomUUID(),
      ...data,
      is_primary_contact: tempFamilyMembers.length === 0 || data.is_primary_contact,
    }

    // If new member is primary, unset others
    if (newMember.is_primary_contact) {
      setTempFamilyMembers((prev) =>
        prev.map((m) => ({ ...m, is_primary_contact: false }))
      )
    }

    setTempFamilyMembers((prev) => [...prev, newMember])
    setIsAddingMember(false)
  }

  const handleUpdateFamilyMember = (data: {
    name: string
    email?: string
    phone?: string
    country_code: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications: boolean
  }) => {
    if (!editingMember) return

    setTempFamilyMembers((prev) =>
      prev.map((m) => {
        if (m.id === editingMember.id) {
          return { ...m, ...data }
        }
        // Unset primary if this member becomes primary
        if (data.is_primary_contact && m.id !== editingMember.id) {
          return { ...m, is_primary_contact: false }
        }
        return m
      })
    )
    setEditingMember(null)
  }

  const handleDeleteFamilyMember = (memberId: string) => {
    const memberToDelete = tempFamilyMembers.find((m) => m.id === memberId)
    setTempFamilyMembers((prev) => {
      const newMembers = prev.filter((m) => m.id !== memberId)
      // If we deleted primary, make first one primary
      if (memberToDelete?.is_primary_contact && newMembers.length > 0 && newMembers[0]) {
        newMembers[0].is_primary_contact = true
      }
      return newMembers
    })
  }

  const handleSetPrimary = (memberId: string) => {
    setTempFamilyMembers((prev) =>
      prev.map((m) => ({ ...m, is_primary_contact: m.id === memberId }))
    )
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    if (!currentOrganization) {
      setError('Selecione uma organização')
      return
    }

    if (tempFamilyMembers.length === 0) {
      setError('Adicione pelo menos um familiar')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)
      const data = form.getValues()

      // Create patient
      const newPatient = await createPatient.mutateAsync({
        full_name: data.full_name,
        birth_date: new Date(data.birth_date).toISOString(),
        address: data.address || null,
        emergency_contact: data.emergency_contact || null,
        emergency_contact_country_code: data.emergency_contact_country_code || '55',
        condition_description: data.condition_description || null,
        medical_history: data.medical_history || null,
        current_medications: data.current_medications
          ? data.current_medications.split('\n').filter((m) => m.trim())
          : null,
      })

      // Create family members using the shared hook
      for (const member of tempFamilyMembers) {
        await familyMembersHook.createMember({
          patient_id: newPatient.id,
          name: member.name,
          email: member.email,
          phone: member.phone,
          country_code: member.country_code,
          relationship: member.relationship,
          is_primary_contact: member.is_primary_contact,
          accepts_notifications: member.accepts_notifications,
        })
      }

      setCreatedPatientId(newPatient.id)
      setCreateStep('scheduling')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cadastrar paciente')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFinish = () => {
    setSuccessMessage('Paciente cadastrado com sucesso!')
    setTimeout(() => {
      onOpenChange(false)
      onSuccess?.()
    }, 1500)
  }

  // ============================================================================
  // Derived State
  // ============================================================================

  const isLoading = isSubmitting || createPatient.isPending || patientSearch.isLinking

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cadastrar Paciente</DialogTitle>
          <DialogDescription>
            Busque um paciente existente ou crie um novo cadastro
          </DialogDescription>
        </DialogHeader>

        {/* Success Message */}
        {successMessage && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!successMessage && (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="search" className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                Buscar Paciente
              </TabsTrigger>
              <TabsTrigger value="create" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Novo Cadastro
              </TabsTrigger>
            </TabsList>

            {/* Search Tab */}
            <TabsContent value="search" className="space-y-4 mt-4">
              <div>
                <Input
                  placeholder="Digite o nome do paciente..."
                  value={patientSearch.searchTerm}
                  onChange={handleSearchChange}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Digite pelo menos 2 caracteres para buscar
                </p>
              </div>

              {/* Search Results */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {patientSearch.isSearching ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : patientSearch.results.length > 0 ? (
                  patientSearch.results.map((patient) => (
                    <PatientSearchResultCard
                      key={patient.id}
                      patient={patient}
                      onSelect={handleLinkToPatient}
                      isLinking={patientSearch.isLinking}
                    />
                  ))
                ) : patientSearch.searchTerm.length >= 2 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhum paciente encontrado</p>
                    <p className="text-sm">
                      Crie um novo cadastro na aba &quot;Novo Cadastro&quot;
                    </p>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            {/* Create Tab */}
            <TabsContent value="create" className="space-y-4 mt-4">
              {/* Step Indicator */}
              <StepIndicator
                steps={WIZARD_STEPS}
                currentStep={createStep}
                completedSteps={
                  createStep === 'scheduling'
                    ? ['patient', 'family']
                    : createStep === 'family'
                      ? ['patient']
                      : []
                }
              />

              {/* Step 1: Patient Data */}
              {createStep === 'patient' && (
                <Form {...form}>
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nome Completo *</FormLabel>
                            <FormControl>
                              <Input placeholder="Nome do paciente" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="birth_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Data de Nascimento *</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Endereço</FormLabel>
                          <FormControl>
                            <Input placeholder="Endereço completo" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="emergency_contact"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>Contato de Emergência</FormLabel>
                          <FormControl>
                            <PhoneInputWithDDI
                              value={field.value ?? ''}
                              countryCode={form.watch('emergency_contact_country_code')}
                              onPhoneChange={field.onChange}
                              onCountryCodeChange={(code) => form.setValue('emergency_contact_country_code', code)}
                              error={fieldState.error?.message}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="condition_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Descrição da Condição</FormLabel>
                          <FormControl>
                            <textarea
                              placeholder="Ex: TEA - Nível 1"
                              className="w-full min-h-[80px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="current_medications"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Medicamentos Atuais</FormLabel>
                          <FormControl>
                            <textarea
                              placeholder="Um medicamento por linha..."
                              className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              )}

              {/* Step 2: Family Members */}
              {createStep === 'family' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {tempFamilyMembers.length > 0
                        ? `${tempFamilyMembers.length} familiar(es) adicionado(s)`
                        : 'Adicione pelo menos um familiar para continuar'}
                    </p>
                  </div>

                  {/* Add/Edit Member Form */}
                  {(isAddingMember || editingMember) && (
                    <FamilyMemberForm
                      initialData={
                        editingMember
                          ? {
                              name: editingMember.name,
                              email: editingMember.email,
                              phone: editingMember.phone,
                              country_code: editingMember.country_code,
                              relationship: editingMember.relationship,
                              is_primary_contact: editingMember.is_primary_contact,
                              accepts_notifications: editingMember.accepts_notifications,
                            }
                          : undefined
                      }
                      onSubmit={editingMember ? handleUpdateFamilyMember : handleAddFamilyMember}
                      onCancel={() => {
                        setIsAddingMember(false)
                        setEditingMember(null)
                      }}
                      isEditing={!!editingMember}
                      existingPrimaryCount={tempFamilyMembers.filter((m) => m.is_primary_contact).length}
                    />
                  )}

                  {/* Family Members List */}
                  {!isAddingMember && !editingMember && (
                    <div className="space-y-2">
                      {tempFamilyMembers.map((member) => (
                        <FamilyMemberCard
                          key={member.id}
                          member={{
                            id: member.id,
                            patient_id: '',
                            profile_id: '',
                            relationship: member.relationship,
                            is_primary_contact: member.is_primary_contact,
                            accepts_notifications: member.accepts_notifications,
                            notification_opt_out_source: null,
                            whatsapp_number: member.phone ?? null,
                            country_code: member.country_code,
                            can_report: true,
                            created_at: '',
                            profiles: {
                              id: '',
                              full_name: member.name,
                              email: member.email ?? '',
                              phone: member.phone ?? null,
                            },
                          }}
                          onEdit={(m) => {
                            const tempMember = tempFamilyMembers.find((tm) => tm.id === m.id)
                            if (tempMember) setEditingMember(tempMember)
                          }}
                          onDelete={handleDeleteFamilyMember}
                          onSetPrimary={handleSetPrimary}
                          compact
                        />
                      ))}

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setIsAddingMember(true)}
                      >
                        Adicionar Familiar
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Scheduling (optional) */}
              {createStep === 'scheduling' && createdPatientId && (
                <div className="space-y-4">
                  <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Paciente cadastrado com sucesso! Agora você pode configurar o agendamento.
                  </div>
                  <ScheduleFormStep patientId={createdPatientId} />
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Footer */}
        {!successMessage && (
          <DialogFooter className="gap-2 sm:gap-0">
            {activeTab === 'create' && (
              <>
                {createStep === 'patient' && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                    >
                      Cancelar
                    </Button>
                    <Button type="button" onClick={handleNextStep}>
                      Próximo
                    </Button>
                  </>
                )}
                {createStep === 'family' && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrevStep}
                      disabled={isAddingMember || !!editingMember}
                    >
                      Voltar
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={
                        isLoading ||
                        isAddingMember ||
                        !!editingMember ||
                        tempFamilyMembers.length === 0
                      }
                    >
                      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Cadastrar
                    </Button>
                  </>
                )}
                {createStep === 'scheduling' && (
                  <Button type="button" onClick={handleFinish}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    Concluir
                  </Button>
                )}
              </>
            )}
            {activeTab === 'search' && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
